import React from 'react';
import { Link } from 'react-router-dom';
import useCarousel from '../hooks/useCarousel';
import styles from '../styles/RecipeCarousel.module.css';

const RecipeCarousel = ({ eyebrow, title, items, ariaLabel, onSelect }) => {
  const {
    activeIndex,
    goTo,
    next,
    previous,
    getOffset,
  } = useCarousel(items.length);
  const activeItem = items[activeIndex] || items[0];

  const selectItem = index => {
    goTo(index);
    onSelect(items[index]);
  };

  const renderAction = () => {
    if (!activeItem) return null;

    const label = activeItem.action || 'View details';

    if (activeItem.to) {
      return <Link className={styles.action} to={activeItem.to}>{label}</Link>;
    }

    if (activeItem.href) {
      return <a className={styles.action} href={activeItem.href}>{label}</a>;
    }

    return (
      <button className={styles.action} type="button" onClick={() => onSelect(activeItem)}>
        {label}
      </button>
    );
  };

  if (!items.length) return null;

  return (
    <section className={styles.section} aria-label={ariaLabel || title}>
      <div className={styles.inner}>
        <header className={styles.heading}>
          {eyebrow && <p className={styles.eyebrow}>{eyebrow}</p>}
          {title && <h2>{title}</h2>}
        </header>

        <div className={styles.stage}>
          <button
            className={`${styles.arrow} ${styles.previous}`}
            type="button"
            onClick={previous}
            aria-label="Previous recipe"
          >
            <span aria-hidden="true">‹</span>
          </button>

          <div className={styles.cards}>
            {items.map((item, itemIndex) => {
              const offset = getOffset(itemIndex);
              const isActive = offset === 0;
              const position = offset <= -2
                ? styles.farLeft
                : offset === -1
                  ? styles.left
                  : offset === 1
                    ? styles.right
                    : offset >= 2
                      ? styles.farRight
                      : styles.featured;

              return (
                <button
                  className={`${styles.card} ${position}`}
                  key={item.id || item.title}
                  type="button"
                  onClick={() => selectItem(itemIndex)}
                  aria-label={`Show ${item.title}`}
                  aria-current={isActive ? 'true' : undefined}
                  tabIndex={isActive ? 0 : -1}
                >
                  <img
                    src={item.image}
                    alt={item.alt || item.title}
                    loading={isActive ? 'eager' : 'lazy'}
                    decoding="async"
                  />
                  <span className={styles.shade} aria-hidden="true" />
                  <span className={styles.cardCopy}>
                    <span className={styles.cardTitle}>{item.title}</span>
                    {item.subtitle && <span className={styles.cardSubtitle}>{item.subtitle}</span>}
                  </span>
                </button>
              );
            })}
          </div>

          <button
            className={`${styles.arrow} ${styles.next}`}
            type="button"
            onClick={next}
            aria-label="Next recipe"
          >
            <span aria-hidden="true">›</span>
          </button>

          <div className={styles.actionWrap}>{renderAction()}</div>
        </div>

        {items.length > 1 && (
          <div className={styles.thumbs} aria-label="Choose a recipe">
            {items.map((item, index) => (
              <button
                className={index === activeIndex ? styles.thumbActive : styles.thumb}
                key={item.id || item.title}
                type="button"
                onClick={() => selectItem(index)}
                aria-label={`Show ${item.title}`}
                aria-pressed={index === activeIndex}
              >
                <img src={item.image} alt="" loading="lazy" decoding="async" />
                <span>{item.title}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

RecipeCarousel.defaultProps = {
  eyebrow: '',
  title: '',
  items: [],
  ariaLabel: 'Recipe carousel',
  onSelect: () => {},
};

export default RecipeCarousel;
